/** False when the value would render as "0.00 GB" - used to hide empty resources. */
export const hasGbValue = (gb) => Number(gb) >= 0.005

export const formatGb = (gb) => `${Number(gb).toFixed(gb < 10 ? 2 : 1)} GB`

/** "24.8 / 40 GB" - consumed/limit pair sharing a single unit suffix. */
export const formatGbPair = (usedGb, limitGb) =>
  `${Number(usedGb).toFixed(usedGb < 10 ? 2 : 1)} / ${Number(limitGb).toFixed(limitGb < 10 ? 2 : 1)} GB`

/**
 * Database and storage share one 40GB quota, so they render as segments of a
 * single stacked bar rather than two bars - these are fixed identity colors
 * (which resource is which), never reassigned by value/rank.
 */
export const RESOURCE_COLORS = {
  database: { bar: 'bg-blue-500', dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
  storage: { bar: 'bg-violet-500', dot: 'bg-violet-500', text: 'text-violet-600 dark:text-violet-400' },
}

export const formatNumber = (n) => new Intl.NumberFormat('pt-BR').format(n)

/** "2026-09" -> "setembro de 2026" */
export const formatPeriodLabel = (period) => {
  if (!period) return ''
  const [year, month] = period.split('-').map(Number)
  if (!year || !month) return period
  const date = new Date(year, month - 1, 1)
  const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date)
  return label
}

/**
 * Projects month-end events usage from the daily average consumed so far
 * this month, and returns a friendly status message for the events card
 * footer warning about whether the contracted franchise looks sufficient.
 *
 * The daily average is `used / daysElapsed`, where daysElapsed counts from
 * the company's first event *this month* (`first_event_at`), not from the
 * 1st - a company only activated/sending events from, say, the 10th has
 * only been active for (today - 10 + 1) days, not the full calendar-to-date.
 * Dividing by calendar days elapsed since the 1st would understate its real
 * daily rate and therefore its month-end projection.
 *
 * `period` ("YYYY-MM") is always the current calendar month in practice -
 * see company_franchise.py::_current_month_start_utc - but this still
 * degrades gracefully (falls back to a full month elapsed) if it's ever a
 * past period, rather than dividing by a days-elapsed count that could
 * exceed the days actually in that month.
 */
export const projectEventsUsage = ({ used, limit, period, first_event_at } = {}) => {
  if (!period || !limit) return null

  const [year, month] = period.split('-').map(Number)
  if (!year || !month) return null

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

  const now = new Date()
  const daysInMonth = new Date(year, month, 0).getDate()
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month

  let daysElapsed = daysInMonth
  if (isCurrentMonth) {
    let periodStart = new Date(year, month - 1, 1)
    if (first_event_at) {
      const firstEventDay = startOfDay(new Date(first_event_at))
      if (!Number.isNaN(firstEventDay.getTime()) && firstEventDay > periodStart) {
        periodStart = firstEventDay
      }
    }
    daysElapsed = Math.max(1, Math.round((startOfDay(now) - periodStart) / 86400000) + 1)
  }

  if (!used || used <= 0 || daysElapsed < 1) {
    return { tone: 'info', message: 'Ainda não há consumo suficiente neste mês para projetar o uso da franquia.' }
  }

  const dailyAverage = used / daysElapsed
  const projectedTotal = Math.round(dailyAverage * daysInMonth)
  const projectedPercentage = (projectedTotal / limit) * 100
  const avgLabel = formatNumber(Math.round(dailyAverage))
  const projectedLabel = formatNumber(projectedTotal)

  // Suggested franchise: this month's projected total plus a 40% headroom
  // margin (peaks/seasonal campaigns/growth), rounded up to the nearest
  // 200-unit package - never sold in arbitrary amounts.
  const EVENTS_PACKAGE_SIZE = 200
  const suggestedLimit = Math.ceil((projectedTotal * 1.4) / EVENTS_PACKAGE_SIZE) * EVENTS_PACKAGE_SIZE
  const suggestedLimitLabel = formatNumber(suggestedLimit)

  if (projectedPercentage >= 100) {
    const overBy = formatNumber(projectedTotal - limit)
    return {
      tone: 'critical',
      message: `No ritmo atual (~${avgLabel} eventos/dia), a franquia deve se esgotar antes do fim do mês - projeção de ${projectedLabel} eventos, ${overBy} acima do limite contratado. Contate o fornecedor para ajuste da franquia contratada.`,
      suggestedLimit,
      suggestedLimitLabel,
    }
  }

  if (projectedPercentage >= 85) {
    return {
      tone: 'warning',
      message: `No ritmo atual (~${avgLabel} eventos/dia), a projeção é fechar o mês com ${projectedLabel} eventos (${projectedPercentage.toFixed(0)}% da franquia). Fique atento para não ultrapassar o limite.`,
      suggestedLimit,
      suggestedLimitLabel,
    }
  }

  return {
    tone: 'ok',
    message: `No ritmo atual (~${avgLabel} eventos/dia), a projeção para o fim do mês é de ${projectedLabel} eventos (${projectedPercentage.toFixed(0)}% da franquia) - dentro do limite contratado.`,
  }
}

/**
 * Usage meters (database/storage/events) share the same good/warning/critical
 * thresholds. `track` is a neutral gray for the bar's background at every
 * status - a same-hue track under a same-hue fill (e.g. red-100 under
 * red-500) reads as a muddy solid block instead of a legible progress bar,
 * especially at low fill levels. `chip` is the tinted background used for
 * icon badges elsewhere (not the bar), where a status tint is appropriate.
 */
export const usageStatus = (percentage) => {
  if (percentage >= 90) {
    return {
      bar: 'bg-gradient-to-r from-red-500 to-rose-500',
      track: 'bg-gray-200 dark:bg-gray-700',
      chip: 'bg-red-100 dark:bg-red-950/40',
      text: 'text-red-600 dark:text-red-400',
      label: 'Crítico',
    }
  }
  if (percentage >= 70) {
    return {
      bar: 'bg-gradient-to-r from-amber-400 to-orange-500',
      track: 'bg-gray-200 dark:bg-gray-700',
      chip: 'bg-amber-100 dark:bg-amber-950/40',
      text: 'text-amber-600 dark:text-amber-400',
      label: 'Atenção',
    }
  }
  return {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-500',
    track: 'bg-gray-200 dark:bg-gray-700',
    chip: 'bg-emerald-100 dark:bg-emerald-950/40',
    text: 'text-emerald-600 dark:text-emerald-400',
    label: 'Saudável',
  }
}

/**
 * Same status tiers as usageStatus(), but for the events meter specifically:
 * forces the amber "Atenção" tier when projectEventsUsage() says the
 * franchise is projected to run out this month (tone 'critical'), even if
 * percentage_used itself is still low enough to read as green - a low
 * "used so far" can still look healthy while already on track to blow
 * through the franchise before the month ends. Never escalates to the red
 * tier on its own; if percentage_used is already high enough for that
 * (real, current usage, not just a projection) usageStatus() already
 * reports it independently of this override.
 */
export const eventsUsageStatus = (percentage, projectionTone) => {
  if (projectionTone === 'critical' && percentage < 70) {
    return usageStatus(70)
  }
  return usageStatus(percentage)
}
